// ── Service de Comissões (núcleo/aplicação) ───────────────────────
// Recebe o repositório via porta (DI) — não conhece Supabase. As funções
// puras (agregações/simulador) não dependem do repositório e ficam
// exportadas direto do módulo.

import {
  calcCommission,
  getBonusForFaturamento,
  getNextBonusTier,
  DEFAULT_RATES,
} from '@utils';
import type { CommissionInput } from '@utils';
import type { CommissionRates } from '@/types/domain';
import type {
  NormalizedCommissionRow,
  CommissionTotals,
  UserProfileRef,
} from '@/types/commission';
import type {
  ICommissionRepository,
  ListCommissionsParams,
} from '../ports/ICommissionRepository';

// ─── Service com dependência do repositório ─────────────────────
export class CommissionService {
  constructor(private readonly repo: ICommissionRepository) {}

  listCommissions = (params: ListCommissionsParams): Promise<NormalizedCommissionRow[]> => {
    if (!params?.workspaceId || !params?.period) return Promise.resolve([]);
    return this.repo.listCommissions(params);
  };

  getCommissionByCard = (cardId: string | null | undefined): Promise<NormalizedCommissionRow | null> => {
    if (!cardId) return Promise.resolve(null);
    return this.repo.getCommissionByCard(cardId);
  };

  getCommissionRates = (workspaceId: string | null | undefined): Promise<CommissionRates> => {
    return this.repo.getCommissionRates(workspaceId);
  };

  setCommissionRates = (workspaceId: string, rates: CommissionRates): Promise<void> => {
    if (!workspaceId) throw new Error('workspaceId é obrigatório.');
    return this.repo.setCommissionRates(workspaceId, rates);
  };

  listUsersByRole = (workspaceId: string, role: 'sdr' | 'closer'): Promise<UserProfileRef[]> => {
    if (!workspaceId || !role) return Promise.resolve([]);
    return this.repo.listUsersByRole(workspaceId, role);
  };
}

// ═══════════════════════════════════════════════════════════════
//  Funções PURAS (sem Supabase / sem repositório)
// ═══════════════════════════════════════════════════════════════

// ─── Agregações para KPIs ───────────────────────────────────────
export const summarizeCommissions = (
  commissions: NormalizedCommissionRow[] = [],
  rates: CommissionRates | null = null,
): CommissionTotals => {
  const safeRates = rates || DEFAULT_RATES;
  const totals: CommissionTotals = {
    faturamento_total: 0,
    closer_commission_total: 0,
    sdr_commission_total:    0,
    closer_faturamento: 0,
    sdr_faturamento:    0,
    count_won: commissions.length,
    count_turbo: 0,
    closer_bonus: 0,
    sdr_bonus: 0,
    next_closer_tier: null,
    next_sdr_tier: null,
  };

  for (const c of commissions) {
    const fat = Number(c.faturamento) || 0;
    totals.faturamento_total       += fat;
    totals.closer_commission_total += Number(c.closer_commission) || 0;
    totals.sdr_commission_total    += Number(c.sdr_commission)    || 0;
    if (c.is_turbo) totals.count_turbo += 1;
    totals.closer_faturamento += fat;
    if (!c.closer_as_sdr) totals.sdr_faturamento += fat;
  }

  totals.closer_bonus = getBonusForFaturamento(totals.closer_faturamento, safeRates.closer_bonus);
  totals.sdr_bonus    = getBonusForFaturamento(totals.sdr_faturamento,    safeRates.sdr_bonus);
  totals.next_closer_tier = getNextBonusTier(totals.closer_faturamento, safeRates.closer_bonus);
  totals.next_sdr_tier    = getNextBonusTier(totals.sdr_faturamento,    safeRates.sdr_bonus);

  return totals;
};

// ─── Agrupamento por parcela (tabelão da página) ────────────────
export interface ParcelaBucket {
  qtd: number;
  faturamento: number;
  comissao: number;
}

export type ParcelaGroups = Record<string, Record<string, ParcelaBucket>>;

export const groupByParcelaAndType = (commissions: NormalizedCommissionRow[] = []): ParcelaGroups => {
  const init = (): Record<string, ParcelaBucket> => ({
    '1':  { qtd: 0, faturamento: 0, comissao: 0 },
    '6':  { qtd: 0, faturamento: 0, comissao: 0 },
    '12': { qtd: 0, faturamento: 0, comissao: 0 },
    '18': { qtd: 0, faturamento: 0, comissao: 0 },
    'bp': { qtd: 0, faturamento: 0, comissao: 0 },
  });

  const groups: ParcelaGroups = {
    perpetuo: init(),
    imersao:  init(),
    ss:       init(),
  };

  for (const c of commissions) {
    const g = groups[c.meeting_type ?? ''] || groups.perpetuo;
    const p = c.parcela || '12';
    if (!g[p]) g[p] = { qtd: 0, faturamento: 0, comissao: 0 };
    g[p].qtd         += 1;
    g[p].faturamento += Number(c.faturamento) || 0;
    g[p].comissao    += Number(c.closer_commission) || 0;
  }

  return groups;
};

// ─── Simulador (puro, sem persistência) ─────────────────────────
export interface SimulationScenario extends CommissionInput {
  qtd?: number;
}

export interface SimulationBreakdownItem extends SimulationScenario {
  closer_rate: number;
  sdr_rate: number;
  closer_as_sdr_extra_rate: number;
  closer_commission: number;
  sdr_commission: number;
  turbo_multiplier_applied: number;
  closer_as_sdr_applied: boolean;
}

export interface SimulationResult {
  faturamento_total: number;
  closer_total: number;
  sdr_total: number;
  closer_bonus: number;
  sdr_bonus: number;
  closer_grand_total: number;
  sdr_grand_total: number;
  breakdown: SimulationBreakdownItem[];
}

export const simulateCommissions = (
  scenarios: SimulationScenario[] = [],
  rates: CommissionRates = DEFAULT_RATES,
): SimulationResult => {
  let faturamento = 0;
  let closerSum = 0;
  let sdrSum    = 0;
  const breakdown: SimulationBreakdownItem[] = [];

  for (const s of scenarios) {
    const qtd = Math.max(1, Number(s.qtd) || 1);
    for (let i = 0; i < qtd; i += 1) {
      const r = calcCommission(s, rates);
      faturamento += Number(s.faturamento) || 0;
      closerSum   += r.closer_commission;
      sdrSum      += r.sdr_commission;
      breakdown.push({ ...s, ...r });
    }
  }

  const closerBonus = getBonusForFaturamento(faturamento, rates.closer_bonus);
  const sdrBonus    = getBonusForFaturamento(faturamento, rates.sdr_bonus);

  return {
    faturamento_total: faturamento,
    closer_total:      closerSum,
    sdr_total:         sdrSum,
    closer_bonus:      closerBonus,
    sdr_bonus:         sdrBonus,
    closer_grand_total: closerSum + closerBonus,
    sdr_grand_total:    sdrSum + sdrBonus,
    breakdown,
  };
};
